import type { JSONContent } from '@tiptap/core';
import { getPreviewContentFromVersionPayload } from '@tiptap-pro/extension-snapshot';
import type { TiptapCollabProvider } from '@tiptap-pro/provider';
import * as Y from 'yjs';

import {
  DocumentSettings,
  readDocumentSettings,
  SETTINGS_MAP_KEY,
} from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';

export interface VersionPreview {
  version: number;
  content: JSONContent;
  settings: DocumentSettings;
}

// Snapshot's own watchPreviewContent returns only the body, but the payload carries the
// version's whole Y.Doc, header and footer included.
export const parseVersionPreview = (payload: string): VersionPreview | null => {
  const parsed = JSON.parse(payload) as { event?: string; version: number; ydoc: string };
  if (parsed.event !== 'version.preview') return null;

  const doc = new Y.Doc();
  Y.applyUpdate(doc, Uint8Array.from(atob(parsed.ydoc), (char) => char.charCodeAt(0)));
  const settings = readDocumentSettings(doc);
  doc.destroy();

  return { version: parsed.version, content: getPreviewContentFromVersionPayload(payload), settings };
};

export const requestVersionPreview = (provider: TiptapCollabProvider, version: number): void => {
  provider.sendStateless(JSON.stringify({ action: 'version.preview', version }));
};

// Snapshot's revertToVersion command restores only the body; the provider can restore other fields too.
export const revertBodyAndSettings = (provider: TiptapCollabProvider, version: number, versionName: string): void => {
  provider.revertToVersion(version, {
    fields: ['default', SETTINGS_MAP_KEY],
    currentVersionName: 'Unversioned changes',
    newVersionName: `Reverted to ${versionName}`,
  });
};

export const watchVersionPreview = (
  provider: TiptapCollabProvider,
  onPreview: (preview: VersionPreview) => void
): (() => void) => {
  const handleStateless = ({ payload }: { payload: string }): void => {
    const preview = parseVersionPreview(payload);
    if (preview) onPreview(preview);
  };
  provider.on('stateless', handleStateless);
  return () => {
    provider.off('stateless', handleStateless);
  };
};
